import { Component, OnInit } from '@angular/core';
import { FormButton } from '../../../core/components/form-field-button/form-field-button.component';
import { Item } from '../../../core/components/listbox/listbox.component';
import { DataTaskService } from '../../../core/services/data-task.service';
import { ModalService } from '../../../core/services/modal.service';
import { StateService } from '../../../core/services/state.service';
import { BusinesscoItem, LogisticService } from '../../../services/logistic.service';
import { CustomerItem, TypeaheadService } from '../../../services/typeahead.service';

@Component({
  selector: 'tt-equipment-edit',
  templateUrl: './equipment-edit.component.html',
  styleUrls: ['./equipment-edit.component.css']
})
export class EquipmentEditComponent implements OnInit {
    equipmentId;

    public factoryButtons: FormButton[] = [{ id: 'list', icon: 'fa fa-bars', type: 'primary', goto: 'factories' }];

    public workGroupButtons: FormButton[] = [{ id: 'list', type: 'primary', icon: 'fa fa-bars', goto: 'workgrps' }];

    public workGroupButtonsExpanded: FormButton[] = [
        { id: 'erase', icon: 'fa fa-eraser', type: 'danger', onClick: () => this.edit!.workgrp_keyno = '0' },
        { id: 'list', type: 'primary', icon: 'fa fa-bars', goto: 'workgrps' }
    ];

    public equipmentGroupButtons: FormButton[] = [{ id: 'list', type: 'primary', icon: 'fa fa-bars', goto: 'equipmentgrps' }];

    public productIdSelectingButtons: FormButton[] = [
        {
            id: 'goto',
            type: 'primary',
            icon: 'fas fa-search',
            goto: 'selectproduct',
            gotoParms: () => ({
                webpage_name: this.stateService.getCurrentName(),
                parm1: this.edit!.equipment_id,
                parm2: '0'
            })
        }
    ];

    public productIdEditingButtons: FormButton[] = [{ id: 'goto', type: 'primary', icon: 'fas fa-chevron-right', goto: 'product', gotoParms: () => this.edit!.prod_id ?? '' }];

    public edit?: Edit;

    public selectListCompanies: BusinesscoItem[] = [];

    public selectListFactories: FactoryItem[] = [];

    public selectListWorkGroups: WorkGroupItem[] = [];

    public selectListStockLocations: StockLocationItem[] = [];

    public selectListEquipmentGroups: Item[] = [];

    public saving = false;

    constructor(private stateService: StateService, private modal: ModalService, private datatask: DataTaskService, private logistic: LogisticService, private typeahead: TypeaheadService) {
        this.equipmentId = this.stateService.getStateParams()['equipment_id'];
    }

    private async loadEdit() {
        if (!!this.equipmentId) {
            this.edit = (await this.datatask.Post(239, { equipment_id: this.equipmentId }))[0];
        }
    }

    private async loadCompanies() {
        this.selectListCompanies = await this.logistic.getCompanies();
    }

    private async loadEquipmentGroups() {
        this.selectListEquipmentGroups = await this.datatask.Post(2047);
    }

    private async loadFactories() {
        if (!!this.edit?.businessco_no) {
            return this.selectListFactories = await this.datatask.Post(2024, { businessco_no: this.edit?.businessco_no });
        }
    }

    private async loadSelectWorkGroups() {
        if (!this.edit) return;

        return this.selectListWorkGroups = await this.datatask.Post(2040, {
            businessco_no: this.edit.businessco_no,
            factory_id: this.edit.factory_id
        });
    }

    private async loadStockLocations() {
        if (!this.edit) return;

        return this.selectListStockLocations = await this.datatask.Post(472, {
            stockzoneclass_keyno: '50',
            factory_id: this.edit.factory_id
        });
    }

    public searchProductNames = () => {
        return this.typeahead.lookUpProducts({ prod_name: this.edit?.prod_name ?? '' });
    }

    public searchSupplierNames = () => {
        return this.typeahead.searchCustomer({
            cust_name: this.edit?.supplier_name ?? '',
            custtype: 'C',
            lastselect: 'SEARCH'
        });
    }

    public onProductNameSelected(event?: Item) {
        if (!this.edit) return;

        this.edit.prod_id = !!event ? '' + event.item_id : '';
        this.edit.prod_name = event?.item_name ?? '';
    }

    public onSupplierNameSelected(event?: CustomerItem) {
        if (!this.edit) return;

        this.edit.supplier_no = !!event ? '' + event.item_id : '0';
        this.edit.supplier_name = event?.item_name ?? '';
    }

    public async saveChanges() {
        if (!!this.edit) {
            try {
                this.saving = true;

                const response = (await this.datatask.Post(249, this.edit))[0];

                if (response?.errorcode !== '0') {
                    this.modal.show({
                        type: 'warning',
                        title: 'error',
                        translateTitle: true,
                        message: response.errormessage
                    });
                } else {
                    this.stateService.back();
                }
            } finally {
                this.saving = false;
            }
        }
    }

    public onChanged(_: string, id: keyof Edit) {
        if (!this.edit) return;

        switch (id) {
            case 'businessco_no':
                this.edit.factory_id = '';
                this.loadFactories();
                this.loadSelectWorkGroups();
                break;
            case 'factory_id':
                this.edit.workgrp_keyno = '0';
                this.loadSelectWorkGroups();
                this.loadStockLocations();
                break;
            default:
                break;
        }
    }

    private async init() {
        await this.loadEdit();
        this.loadCompanies();
        this.loadEquipmentGroups();
        this.loadFactories();
        this.loadSelectWorkGroups();
        this.loadStockLocations();
    }

    ngOnInit() {
        this.init();
    }
}

interface Edit {
    equipment_id: string;
    equipment_name: string;
    equipment_not_in_use: string;
    businessco_no: string;
    newrecord: string;
    prod_id: string;
    prod_name: string;
    prod_keyno: string;
    supplier_name: string;
    supplier_no: string;
    loading_height_cm: string;
    loading_capasity_m3: string;
    nbr_of_custacts: string;
    workgrp_keyno: string;
    factory_id: string;
    equipmentgrp_id: string;
    nbr_of_operations: string;
    show_setting: string;
    show_workgrp_keyno: string;
    cost_pr_hour: string;
    stocklocation_keyno: string;
    maintainance_description: string;
    productionyear: string;
}

interface FactoryItem {
    factory_id: string;
    factory_name: string;
    orderby: number;
}

interface WorkGroupItem {
    workgrp_keyno: string;
    workgrp_name: string;
    orderby: number;
}

interface StockLocationItem {
    stocklocation_keyno: string;
    stocklocation_name: string;
    orderby: number;
}
